// =============================================================================
// Origin UI — src/pages/admin/ExchangeRateAdminTab.tsx
// Admin Config sub-tab for managing exchange rates used in letter generation.
// Lists all rates, add new, edit existing. Fixed rates are protected.
// =============================================================================

import { useState, useEffect } from 'react';
import { letterApi, ExchangeRate } from '@/services/letterApi';
import { Card, ErrorBanner, LoadingSpinner } from '@/components/common';
import styles from './admin.module.css';

export function ExchangeRateAdminTab() {
  const [rates, setRates] = useState<ExchangeRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState<ExchangeRate | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    fromCurrency: '',
    toCurrency: '',
    rate: '',
    effectiveDate: '',
    isFixed: false,
  });

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoading(true);
      setError('');
      const data = await letterApi.getExchangeRates();
      setRates(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load exchange rates');
    } finally {
      setLoading(false);
    }
  }

  function startCreate() {
    setForm({ fromCurrency: 'USD', toCurrency: '', rate: '', effectiveDate: new Date().toISOString().slice(0, 10), isFixed: false });
    setCreating(true);
    setEditing(null);
    setSuccess('');
  }

  function startEdit(rate: ExchangeRate) {
    if (rate.isFixed) return; // Fixed rates cannot be edited
    setForm({
      fromCurrency: rate.fromCurrency,
      toCurrency: rate.toCurrency,
      rate: rate.rate.toString(),
      effectiveDate: rate.effectiveDate.slice(0, 10),
      isFixed: rate.isFixed,
    });
    setEditing(rate);
    setCreating(false);
    setSuccess('');
  }

  function cancel() {
    setCreating(false);
    setEditing(null);
    setForm({ fromCurrency: '', toCurrency: '', rate: '', effectiveDate: '', isFixed: false });
  }

  async function save() {
    if (!form.fromCurrency || !form.toCurrency || !form.rate || !form.effectiveDate) {
      setError('All fields are required');
      return;
    }
    const rateNum = parseFloat(form.rate);
    if (isNaN(rateNum) || rateNum <= 0) {
      setError('Rate must be a positive number');
      return;
    }

    try {
      setSaving(true);
      setError('');
      if (editing) {
        await letterApi.updateExchangeRate(editing.exchangeRateId, {
          rate: rateNum,
          effectiveDate: form.effectiveDate,
        });
      } else {
        await letterApi.createExchangeRate({
          fromCurrency: form.fromCurrency.toUpperCase(),
          toCurrency: form.toCurrency.toUpperCase(),
          rate: rateNum,
          effectiveDate: form.effectiveDate,
          isFixed: form.isFixed,
        });
      }
      cancel();
      setSuccess(editing ? 'Rate updated successfully.' : 'Rate added successfully.');
      setTimeout(() => setSuccess(''), 3000);
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {success && (
        <div style={{ padding: '10px 16px', marginBottom: 16, borderRadius: 6, background: 'var(--green-light)', color: 'var(--green)', fontSize: 13, fontWeight: 600 }}>
          {success}
        </div>
      )}

      <div className={styles.toolBar}>
        <span className={styles.count}>{rates.length} exchange rates</span>
        <button className={styles.addBtn} onClick={startCreate}>+ Add Rate</button>
      </div>

      <p className={styles.muted} style={{ marginBottom: 16 }}>
        Exchange rates are used when generating salary letters in a different currency than the employee's contract currency.
        Fixed rates (e.g. USD/AED pegged at 3.6725) cannot be edited.
      </p>

      {/* Create form */}
      {creating && (
        <Card className={styles.formCard} padding={true}>
          <h4 className={styles.formTitle}>Add Exchange Rate</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>From Currency *</label>
              <input
                className={styles.fieldInput}
                type="text"
                value={form.fromCurrency}
                onChange={e => setForm({ ...form, fromCurrency: e.target.value })}
                placeholder="USD"
                maxLength={3}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>To Currency *</label>
              <input
                className={styles.fieldInput}
                type="text"
                value={form.toCurrency}
                onChange={e => setForm({ ...form, toCurrency: e.target.value })}
                placeholder="AED"
                maxLength={3}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Rate *</label>
              <input
                className={styles.fieldInput}
                type="number"
                step="0.000001"
                value={form.rate}
                onChange={e => setForm({ ...form, rate: e.target.value })}
                placeholder="3.6725"
              />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Effective Date *</label>
              <input
                className={styles.fieldInput}
                type="date"
                value={form.effectiveDate}
                onChange={e => setForm({ ...form, effectiveDate: e.target.value })}
              />
            </div>
            <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--navy)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.isFixed}
                  onChange={e => setForm({ ...form, isFixed: e.target.checked })}
                />
                Fixed rate (pegged currency — will not change)
              </label>
            </div>
          </div>
          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={cancel}>Cancel</button>
            <button className={styles.saveBtn} onClick={save} disabled={saving}>
              {saving ? 'Saving...' : 'Add Rate'}
            </button>
          </div>
        </Card>
      )}

      {/* Edit form */}
      {editing && (
        <Card className={styles.formCard} padding={true}>
          <h4 className={styles.formTitle}>Edit Rate: {editing.fromCurrency} → {editing.toCurrency}</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Rate *</label>
              <input
                className={styles.fieldInput}
                type="number"
                step="0.000001"
                value={form.rate}
                onChange={e => setForm({ ...form, rate: e.target.value })}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Effective Date *</label>
              <input
                className={styles.fieldInput}
                type="date"
                value={form.effectiveDate}
                onChange={e => setForm({ ...form, effectiveDate: e.target.value })}
              />
            </div>
          </div>
          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={cancel}>Cancel</button>
            <button className={styles.saveBtn} onClick={save} disabled={saving}>
              {saving ? 'Saving...' : 'Update Rate'}
            </button>
          </div>
        </Card>
      )}

      {/* Rates table */}
      {rates.length === 0 && !creating && (
        <Card>
          <p className={styles.muted} style={{ textAlign: 'center', padding: 20 }}>
            No exchange rates configured. Click "+ Add Rate" to add one.
          </p>
        </Card>
      )}

      {rates.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>From</th>
                <th>To</th>
                <th>Rate</th>
                <th>Effective Date</th>
                <th>Type</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rates.map(rate => (
                <tr key={rate.exchangeRateId}>
                  <td style={{ fontWeight: 600 }}>{rate.fromCurrency}</td>
                  <td style={{ fontWeight: 600 }}>{rate.toCurrency}</td>
                  <td className={styles.mono}>{rate.rate.toFixed(6)}</td>
                  <td>{new Date(rate.effectiveDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                  <td>
                    {rate.isFixed ? (
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#E3F5F6', color: '#00B0B9' }}>
                        Fixed
                      </span>
                    ) : (
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#F5F7FA', color: '#7A8A9E' }}>
                        Variable
                      </span>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--gray-400)' }}>
                    {rate.updatedBy && `${rate.updatedBy}`}
                  </td>
                  <td>
                    {!rate.isFixed && (
                      <button
                        className={styles.editBtn || styles.cancelBtn}
                        style={{ padding: '4px 10px', fontSize: 12 }}
                        onClick={() => startEdit(rate)}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
